import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface QueryConfig {
  key: string[];
  name: string;
}

interface StaleQuery {
  key: string[];
  name: string;
  lastUpdatedAt: number;
  staleDurationMs: number;
}

interface StaleQueryAlertComponentProps {
  queries: QueryConfig[];
  stalenessThresholdMs?: number;
}

export function StaleQueryAlertComponent({
  queries,
  stalenessThresholdMs = 60000, // 1 minute
}: StaleQueryAlertComponentProps) {
  const queryClient = useQueryClient();
  const [staleQueries, setStaleQueries] = useState<StaleQuery[]>([]);

  useEffect(() => {
    const checkStaleness = () => {
      const now = Date.now();
      const stale: StaleQuery[] = [];

      queries.forEach((queryConfig) => {
        const queryState = queryClient.getQueryState(queryConfig.key);

        if (queryState && queryState.dataUpdatedAt) {
          const timeSinceUpdate = now - queryState.dataUpdatedAt;

          if (timeSinceUpdate > stalenessThresholdMs) {
            stale.push({
              key: queryConfig.key,
              name: queryConfig.name,
              lastUpdatedAt: queryState.dataUpdatedAt,
              staleDurationMs: timeSinceUpdate,
            });
          }
        }
      });

      setStaleQueries(stale);
    };

    checkStaleness();

    const interval = setInterval(checkStaleness, 5000);

    return () => clearInterval(interval);
  }, [queryClient, queries, stalenessThresholdMs]);

  if (staleQueries.length === 0) {
    return null;
  }

  return (
    <Alert variant="destructive">
      <AlertDescription>
        <div className="flex flex-col gap-2">
          <div>⚠️ Stale data detected: {staleQueries.map((q) => q.name).join(", ")}</div>

          <div className="text-xs text-muted-foreground">
            {staleQueries.map((q) => (
              <div key={q.name}>
                {q.name}: Last updated{" "}
                {q.staleDurationMs >= 60000
                  ? `${Math.round(q.staleDurationMs / 60000)} minute(s) ago`
                  : `${Math.round(q.staleDurationMs / 1000)} second(s) ago`}
              </div>
            ))}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
